/**
 *
 * Provides fake authentication over Cognito (Amplify v6)
 */

import Validation from '~/util/Validation';
import {
  INVALID_PARAMETER,
  INVALID_PARAMETER_FORGOT_PASS,
  USER_DOES_NOT_EXIST,
  INCORRECT_USERNAME_OR_PASSWORD,
  USER_UNCONFIRMED,
  USERNAME_EXISTS,
  REGISTRATION_OK,
  LOGIN_OK,
  VERIFICATION_NON_EXISTENT_USER,
  VERIFICATION_INVALID_TOKEN,
  VALIDATION_ALREADY_VALIDATED,
  NEW_PASSWORD_REQUIRED,
  USER_NOT_ACTIVATED,
  EXPIRED_PASSWORD,
} from './CognitoResponse';
import { SIGN_IN_STEP } from '../constants';
import type { AuthSession, AuthUser } from 'aws-amplify/auth';

const DEFAULT_AUTH_DELAY = 50;

export const delayedResolve = (
  value?: any,
  delay: number = DEFAULT_AUTH_DELAY,
): Promise<any> =>
  new Promise(resolve => {
    setTimeout(() => resolve(value), delay);
  });

export const delayedReject = (
  value?: any,
  delay: number = DEFAULT_AUTH_DELAY,
): Promise<any> =>
  new Promise((_, reject) => {
    setTimeout(() => reject(value), delay);
  });

type UserType = {
  email: string;
  password: string;
  token: number;
  name?: string;
  phone?: string;
};

export const USER: {
  [key: string]: UserType;
} = {
  UNCONFIRMED: {
    email: 'unconfirmed@bndl.nl',
    password: 'Pass%1234567',
    token: 123723,
    name: 'Philipp Beau',
    phone: '3806685012345',
  },
  NON_EXISTENT: {
    email: 'non-existent-user_mock@bndl.nl',
    password: 'Pass%1234567',
    token: 123623,
    name: 'Philipp Beau',
    phone: '3806685012345',
  },
  VALID: {
    email: 'rob@bndl.nl',
    password: 'Pass%1234567',
    token: 125123,
    name: 'Rob van Kruijsdijk',
    phone: '3806685012345',
  },
  INVITED_USER: {
    email: 'inviteduser@bndl.nl',
    password: 'Pass!1234567',
    token: 125123,
    name: 'Rob van Kruijsdijk',
    phone: '3806685012345',
  },
  EXPIRED_USER: {
    email: 'expired_user@bndl.nl',
    password: 'Pass!1234567',
    token: 125123,
    name: 'Ramon Gebben',
    phone: '3806685012345',
  },
  /** User without MFA setup - will trigger CONTINUE_SIGN_IN_WITH_TOTP_SETUP on login */
  NO_MFA: {
    email: 'no-mfa@bndl.nl',
    password: 'Pass%1234567',
    token: 123456,
    name: 'No MFA User',
    phone: '3806685012345',
  },
  /** User with MFA setup - will trigger CONFIRM_SIGN_IN_WITH_TOTP_CODE on login */
  WITH_MFA: {
    email: 'with-mfa@bndl.nl',
    password: 'Pass%1234567',
    token: 654321,
    name: 'With MFA User',
    phone: '3806685012345',
  },
  /** User with both TOTP and SMS enabled - triggers CONTINUE_SIGN_IN_WITH_MFA_SELECTION on login */
  WITH_TOTP_AND_SMS: {
    email: 'with-totp-and-sms@bndl.nl',
    password: 'Pass%1234567',
    token: 654322,
    name: 'Dual MFA User',
    phone: '3806685012345',
  },
};

const VALID_TOTP_CODE = '123456';

const FakeUser: AuthUser = {
  username: '4e8b7741-cb9a-432f-8e92-9bfba0e4d1ad',
  userId: '4e8b7741-cb9a-432f-8e92-9bfba0e4d1ad',
  signInDetails: {
    loginId: 'philipp@bndl.nl',
    authFlowType: 'USER_SRP_AUTH',
  },
};

const FakeSession: AuthSession = {
  tokens: {
    accessToken: {
      toString: () => 'DEV_JWT_TOKEN',
      payload: {
        sub: '4e8b7741-cb9a-432f-8e92-9bfba0e4d1ad',
        aud: '4bijda6vkc8qubfke287jokv18',
        'custom:phone': '0683830330',
        email_verified: true,
        token_use: 'access',
        auth_time: 1620900861,
        iss: 'https://cognito-idp.eu-west-1.amazonaws.com/eu-west-1_G4pGMV682',
        name: 'Philipp Beau',
        'cognito:username': '4e8b7741-cb9a-432f-8e92-9bfba0e4d1ad',
        exp: 1622798662,
        iat: 1622795062,
        email: 'philipp@bndl.nl',
      },
    },
  },
};

const FakeUserAttributes = {
  sub: '4e8b7741-cb9a-432f-8e92-9bfba0e4d1ad',
  name: 'Bruce Banner',
  email: 'angrygreenman@gmail.com',
  'custom:phone': '0683830330',
  email_verified: 'true',
  // Additional attributes for testing
  offices: [
    {
      id: 'office-1',
      name: 'Amsterdam',
    },
    {
      id: 'office-2',
      name: 'Utrecht',
    },
  ],
  users: [
    {
      id: 'u---user-1',
      name: 'Bruce Banner',
    },
    {
      id: 'u---user-2',
      name: 'Peter Parker',
    },
    {
      id: 'u---user-3',
      name: 'Matthew Murdock',
    },
  ],
};

/** Amplify v6 Auth functions */
const Auth = {
  _loggedIn: !!sessionStorage.getItem('dhAuth'),
  _mfaSetupPending: false,
  _mfaVerificationPending: false,
  _mfaSelectionPending: false,
  _mfaEnabled: !!sessionStorage.getItem('dhMfaEnabled'),

  getCurrentUser: (): Promise<AuthUser> => {
    if (!Auth._loggedIn) {
      return delayedReject('User is not authenticated');
    }
    return delayedResolve(FakeUser, 0);
  },

  fetchAuthSession: (): Promise<any> => {
    if (!Auth._loggedIn) {
      return delayedReject('No current session');
    }
    return delayedResolve(FakeSession, 0);
  },

  fetchUserAttributes: (): Promise<any> => {
    if (!Auth._loggedIn) {
      return delayedReject('User is not authenticated');
    }
    return delayedResolve(FakeUserAttributes, 0);
  },

  signIn: ({
    username,
    password,
  }: {
    username: string;
    password: string;
  }): Promise<any> => {
    if (username === USER.NON_EXISTENT.email) {
      return delayedReject({
        name: 'UserNotFoundException',
        ...USER_DOES_NOT_EXIST,
      });
    }

    if (username === USER.UNCONFIRMED.email) {
      return delayedReject({
        name: 'UserNotConfirmedException',
        ...USER_UNCONFIRMED,
      });
    }

    if (username === USER.VALID.email) {
      if (password !== USER.VALID.password) {
        return delayedReject({
          name: 'NotAuthorizedException',
          ...INCORRECT_USERNAME_OR_PASSWORD,
        });
      }

      Auth._loggedIn = true;
      sessionStorage.setItem('dhAuth', 'loggedIn');
      return delayedResolve({
        isSignedIn: true,
        nextStep: { signInStep: SIGN_IN_STEP.DONE },
        ...LOGIN_OK,
      });
    }

    if (
      username === USER.EXPIRED_USER.email &&
      password === USER.EXPIRED_USER.password
    ) {
      return delayedReject({
        name: 'PasswordResetRequiredException',
        ...EXPIRED_PASSWORD,
      });
    }

    if (
      username === USER.INVITED_USER.email &&
      password === USER.INVITED_USER.password
    ) {
      return delayedResolve({
        isSignedIn: false,
        nextStep: {
          signInStep: SIGN_IN_STEP.CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED,
          missingAttributes: [],
        },
        ...NEW_PASSWORD_REQUIRED,
      });
    }

    if (username === USER.NO_MFA.email && password === USER.NO_MFA.password) {
      Auth._loggedIn = true;
      Auth._mfaSetupPending = true;
      sessionStorage.setItem('dhAuth', 'loggedIn');
      return delayedResolve({
        isSignedIn: false,
        nextStep: {
          signInStep: SIGN_IN_STEP.CONTINUE_SIGN_IN_WITH_TOTP_SETUP,
        },
      });
    }

    if (
      username === USER.WITH_MFA.email &&
      password === USER.WITH_MFA.password
    ) {
      Auth._loggedIn = true;
      Auth._mfaVerificationPending = true;
      sessionStorage.setItem('dhAuth', 'loggedIn');
      return delayedResolve({
        isSignedIn: false,
        nextStep: {
          signInStep: SIGN_IN_STEP.CONFIRM_SIGN_IN_WITH_TOTP_CODE,
        },
      });
    }

    if (
      username === USER.WITH_TOTP_AND_SMS.email &&
      password === USER.WITH_TOTP_AND_SMS.password
    ) {
      Auth._loggedIn = true;
      Auth._mfaSelectionPending = true;
      sessionStorage.setItem('dhAuth', 'loggedIn');
      return delayedResolve({
        isSignedIn: false,
        nextStep: {
          signInStep: SIGN_IN_STEP.CONTINUE_SIGN_IN_WITH_MFA_SELECTION,
          allowedMFATypes: ['TOTP', 'SMS'],
        },
      });
    }

    return delayedReject({
      name: 'UserNotFoundException',
      ...USER_DOES_NOT_EXIST,
    });
  },

  signUp: ({
    username,
    password,
    options,
  }: {
    username: string;
    password: string;
    options?: {
      userAttributes?: Record<string, string>;
      autoSignIn?: boolean;
    };
  }): Promise<any> => {
    if (!Validation.Email.isValid(username)) {
      return delayedReject(INVALID_PARAMETER);
    }

    if (!Validation.Password.isValid(password)) {
      return delayedReject(INVALID_PARAMETER);
    }

    if (
      options?.userAttributes?.name == null ||
      !Validation.String.isString(options.userAttributes.name)
    ) {
      return delayedReject(INVALID_PARAMETER);
    }

    if (username === 'rob@bndl.nl') {
      return delayedReject(USERNAME_EXISTS);
    }

    return delayedResolve({
      isSignUpComplete: false,
      nextStep: {
        signUpStep: 'CONFIRM_SIGN_UP',
        codeDeliveryDetails: {
          destination: username,
          deliveryMedium: 'EMAIL',
        },
      },
      userId: 'fake-user-id',
      ...REGISTRATION_OK,
    });
  },

  confirmSignUp: ({
    username,
    confirmationCode,
  }: {
    username: string;
    confirmationCode: string;
  }): Promise<any> => {
    if (username === USER.VALID.email) {
      return delayedReject({
        name: 'AliasExistsException',
        ...VALIDATION_ALREADY_VALIDATED,
      });
    }

    if (username === USER.NON_EXISTENT.email) {
      return delayedReject({
        name: 'UserNotFoundException',
        ...VERIFICATION_NON_EXISTENT_USER,
      });
    }

    if (username === USER.UNCONFIRMED.email) {
      if (parseInt(confirmationCode) !== USER.UNCONFIRMED.token) {
        return delayedReject({
          name: 'CodeMismatchException',
          ...VERIFICATION_INVALID_TOKEN,
        });
      }

      return delayedResolve({
        isSignUpComplete: true,
        nextStep: { signUpStep: SIGN_IN_STEP.DONE },
      });
    }

    return delayedReject({
      name: 'UserNotFoundException',
      ...VERIFICATION_NON_EXISTENT_USER,
    });
  },

  resendSignUpCode: ({ username }: { username: string }): Promise<any> =>
    delayedResolve({
      destination: username,
      deliveryMedium: 'EMAIL',
      attributeName: 'email',
    }),

  resetPassword: ({ username }: { username: string }): Promise<any> => {
    if (username === USER.NON_EXISTENT.email) {
      return delayedReject({
        name: 'UserNotFoundException',
        ...USER_DOES_NOT_EXIST,
      });
    }

    if (username === USER.UNCONFIRMED.email) {
      return delayedReject(INVALID_PARAMETER_FORGOT_PASS);
    }

    if (username === USER.INVITED_USER.email) {
      return delayedReject({
        name: 'UserNotConfirmedException',
        ...USER_NOT_ACTIVATED,
      });
    }

    return delayedResolve({
      isPasswordReset: false,
      nextStep: {
        resetPasswordStep: 'CONFIRM_RESET_PASSWORD_WITH_CODE',
        codeDeliveryDetails: {
          destination: username,
          deliveryMedium: 'EMAIL',
        },
      },
    });
  },

  confirmResetPassword: ({
    username,
    confirmationCode,
    newPassword,
  }: {
    username: string;
    confirmationCode: string;
    newPassword: string;
  }): Promise<any> => {
    if (confirmationCode !== USER.VALID.token.toString()) {
      return delayedReject({
        name: 'CodeMismatchException',
        ...VERIFICATION_INVALID_TOKEN,
      });
    }

    const result = username + confirmationCode + newPassword;
    return delayedResolve({
      isPasswordReset: true,
      nextStep: { resetPasswordStep: SIGN_IN_STEP.DONE },
      result,
    });
  },

  confirmSignIn: ({
    challengeResponse,
    options,
  }: {
    challengeResponse: string;
    options?: any;
  }): Promise<any> => {
    if (Auth._mfaSelectionPending) {
      if (challengeResponse === 'TOTP') {
        Auth._mfaSelectionPending = false;
        Auth._mfaVerificationPending = true;
        return delayedResolve({
          isSignedIn: false,
          nextStep: {
            signInStep: SIGN_IN_STEP.CONFIRM_SIGN_IN_WITH_TOTP_CODE,
          },
        });
      }
      return delayedReject({
        name: 'InvalidParameterException',
        message: 'Unsupported MFA type selection',
      });
    }

    if (Auth._mfaVerificationPending) {
      if (challengeResponse === VALID_TOTP_CODE) {
        Auth._mfaVerificationPending = false;
        Auth._loggedIn = true;
        sessionStorage.setItem('dhAuth', 'loggedIn');
        return delayedResolve({
          isSignedIn: true,
          nextStep: { signInStep: SIGN_IN_STEP.DONE },
          ...LOGIN_OK,
        });
      } else {
        return delayedReject({
          name: 'CodeMismatchException',
          message: 'Invalid verification code provided',
        });
      }
    }

    if (challengeResponse && options) {
      Auth._loggedIn = true;
      sessionStorage.setItem('dhAuth', 'loggedIn');
      return delayedResolve({
        isSignedIn: true,
        nextStep: { signInStep: SIGN_IN_STEP.DONE },
        ...LOGIN_OK,
      });
    } else {
      return delayedReject(INVALID_PARAMETER);
    }
  },

  updatePassword: ({
    oldPassword,
    newPassword,
  }: {
    oldPassword: string;
    newPassword: string;
  }): Promise<any> => {
    if (!Auth._loggedIn) {
      return delayedReject({
        name: 'NotAuthorizedException',
        message: 'User is not authenticated',
      });
    }

    if (USER.VALID.password === oldPassword && newPassword) {
      return delayedResolve({ message: 'Password updated successfully' });
    } else {
      return delayedReject(INVALID_PARAMETER);
    }
  },

  signOut: (): Promise<any> => {
    Auth._loggedIn = false;
    Auth._mfaSetupPending = false;
    Auth._mfaVerificationPending = false;
    Auth._mfaSelectionPending = false;
    sessionStorage.removeItem('dhAuth');
    return delayedResolve({ isSignedOut: true });
  },

  setUpTOTP: (): Promise<{ sharedSecret: string }> => {
    if (!Auth._loggedIn) {
      return delayedReject({
        name: 'NotAuthorizedException',
        message: 'User is not authenticated',
      });
    }
    return delayedResolve({
      sharedSecret: 'FAKE_TOTP_SECRET_FOR_TESTING',
    });
  },

  verifyTOTPSetup: ({ code }: { code: string }): Promise<void> => {
    if (!Auth._loggedIn) {
      return delayedReject({
        name: 'NotAuthorizedException',
        message: 'User is not authenticated',
      });
    }
    if (code === VALID_TOTP_CODE) {
      Auth._mfaSetupPending = false;
      Auth._mfaEnabled = true;
      sessionStorage.setItem('dhMfaEnabled', 'true');
      return delayedResolve(undefined);
    } else {
      return delayedReject({
        name: 'CodeMismatchException',
        message: 'Invalid verification code provided',
      });
    }
  },

  updateMFAPreference: ({
    totp,
    sms,
  }: {
    totp?: 'PREFERRED' | 'ENABLED' | 'DISABLED' | 'NOT_PREFERRED';
    sms?: 'PREFERRED' | 'ENABLED' | 'DISABLED' | 'NOT_PREFERRED';
  }): Promise<void> => {
    if (!Auth._loggedIn) {
      return delayedReject({
        name: 'NotAuthorizedException',
        message: 'User is not authenticated',
      });
    }
    const enablesMfa =
      totp === 'PREFERRED' ||
      totp === 'ENABLED' ||
      sms === 'PREFERRED' ||
      sms === 'ENABLED';
    const disablesTotp = totp === 'DISABLED' || totp === 'NOT_PREFERRED';
    const disablesSms = sms === 'DISABLED' || sms === 'NOT_PREFERRED';

    if (enablesMfa) {
      Auth._mfaEnabled = true;
      sessionStorage.setItem('dhMfaEnabled', 'true');
    } else if (disablesTotp && disablesSms) {
      Auth._mfaEnabled = false;
      sessionStorage.removeItem('dhMfaEnabled');
    }
    return delayedResolve(undefined);
  },

  updateUserAttribute: (_input: {
    userAttribute: { attributeKey: string; value: string };
  }): Promise<{ nextStep: { updateAttributeStep: string } }> => {
    if (!Auth._loggedIn) {
      return delayedReject({
        name: 'NotAuthorizedException',
        message: 'User is not authenticated',
      });
    }
    return delayedResolve({
      nextStep: { updateAttributeStep: 'CONFIRM_ATTRIBUTE_WITH_CODE' },
    });
  },

  fetchMFAPreference: (): Promise<{
    enabled?: Array<'TOTP' | 'SMS'>;
    preferred?: 'TOTP' | 'SMS';
  }> => {
    if (!Auth._loggedIn) {
      return delayedReject({
        name: 'NotAuthorizedException',
        message: 'User is not authenticated',
      });
    }
    if (Auth._mfaEnabled) {
      return delayedResolve({
        enabled: ['TOTP', 'SMS'],
        preferred: 'TOTP' as const,
      });
    }
    return delayedResolve({
      enabled: [],
      preferred: undefined,
    });
  },
};

export default Auth;
